// packages/frontend/src/utils/performance.ts

import { logger } from "./logger";

interface PerformanceMetrics {
  loadTime: number
  renderTime: number
  bundleSize: number
  memoryUsage: number
  networkRequests: number
  cacheHitRate: number
}

interface PerformanceEntry {
  name: string
  startTime: number
  endTime?: number
  duration?: number
  type: 'navigation' | 'resource' | 'measure' | 'paint'
}

class PerformanceMonitor {
  private metrics: PerformanceMetrics
  private entries: PerformanceEntry[] = []
  private observers: PerformanceObserver[] = []

  constructor() {
    this.metrics = {
      loadTime: 0,
      renderTime: 0,
      bundleSize: 0,
      memoryUsage: 0,
      networkRequests: 0,
      cacheHitRate: 0
    }
    this.initializeObservers()
  }

  private initializeObservers() {
    // Observe navigation timing
    if ('PerformanceObserver' in window) {
      const navObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        entries.forEach((entry) => {
          if (entry.entryType === 'navigation') {
            this.metrics.loadTime = entry.duration
            this.trackEntry({
              name: 'page-load',
              startTime: entry.startTime,
              endTime: entry.startTime + entry.duration,
              duration: entry.duration,
              type: 'navigation'
            })
          }
        })
      })
      
      try {
        navObserver.observe({ entryTypes: ['navigation'] })
        this.observers.push(navObserver)
      } catch (error) {
        logger.warn('Navigation timing not supported:', error)
      }

      // Observe resource timing
      const resourceObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        entries.forEach((entry) => {
          if (entry.entryType === 'resource') {
            this.metrics.networkRequests++
            this.trackEntry({
              name: entry.name,
              startTime: entry.startTime,
              endTime: entry.startTime + entry.duration,
              duration: entry.duration,
              type: 'resource'
            })
          }
        })
      })
      
      try {
        resourceObserver.observe({ entryTypes: ['resource'] })
        this.observers.push(resourceObserver)
      } catch (error) {
        logger.warn('Resource timing not supported:', error)
      }

      // Observe paint timing
      const paintObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        entries.forEach((entry) => {
          if (entry.entryType === 'paint') {
            this.metrics.renderTime = entry.startTime
            this.trackEntry({
              name: entry.name,
              startTime: entry.startTime,
              endTime: entry.startTime,
              duration: 0,
              type: 'paint'
            })
          }
        })
      })
      
      try {
        paintObserver.observe({ entryTypes: ['paint'] })
        this.observers.push(paintObserver)
      } catch (error) {
        logger.warn('Paint timing not supported:', error)
      }
    }
  }

  private trackEntry(entry: PerformanceEntry) {
    this.entries.push(entry)
    
    // Keep only last 100 entries to prevent memory leaks
    if (this.entries.length > 100) {
      this.entries = this.entries.slice(-100)
    }
  }

  public startMeasure(name: string): void {
    if ('performance' in window && 'mark' in performance) {
      performance.mark(`${name}-start`)
    }
  }

  public endMeasure(name: string): number {
    if ('performance' in window && 'mark' in performance && 'measure' in performance) {
      performance.mark(`${name}-end`)
      performance.measure(name, `${name}-start`, `${name}-end`)
      
      const measure = performance.getEntriesByName(name, 'measure')[0]
      if (measure) {
        this.trackEntry({
          name,
          startTime: measure.startTime,
          endTime: measure.startTime + measure.duration,
          duration: measure.duration,
          type: 'measure'
        })
        return measure.duration
      }
    }
    return 0
  }

  public getMetrics(): PerformanceMetrics {
    // Update memory usage if available
    if ('memory' in performance) {
      this.metrics.memoryUsage = (performance as any).memory.usedJSHeapSize
    }

    // Calculate cache hit rate
    const resourceEntries = this.entries.filter(entry => entry.type === 'resource')
    const cachedEntries = resourceEntries.filter(entry => 
      entry.name.includes('from-cache') || entry.duration === 0
    )
    this.metrics.cacheHitRate = resourceEntries.length > 0 
      ? (cachedEntries.length / resourceEntries.length) * 100 
      : 0

    return { ...this.metrics }
  }

  public getEntries(): PerformanceEntry[] {
    return [...this.entries]
  }

  public getEntriesByType(type: PerformanceEntry['type']): PerformanceEntry[] {
    return this.entries.filter(entry => entry.type === type)
  }

  public getSlowEntries(threshold: number = 1000): PerformanceEntry[] {
    return this.entries.filter(entry => (entry.duration || 0) > threshold)
  }

  public clearEntries(): void {
    this.entries = []
    if ('performance' in window && 'clearMarks' in performance) {
      performance.clearMarks()
      performance.clearMeasures()
    }
  }

  public disconnect(): void {
    this.observers.forEach(observer => observer.disconnect())
    this.observers = []
  }

  public reportToAnalytics(): void {
    const metrics = this.getMetrics()
    const slowEntries = this.getSlowEntries()
    
    // Report to analytics service if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'performance_metrics', {
        load_time: metrics.loadTime,
        render_time: metrics.renderTime,
        memory_usage: metrics.memoryUsage,
        network_requests: metrics.networkRequests,
        cache_hit_rate: metrics.cacheHitRate,
        slow_entries_count: slowEntries.length
      })
    }

    // Log performance issues
    if (metrics.loadTime > 3000) {
      logger.warn('Slow page load detected:', metrics.loadTime + 'ms')
    }
    
    if (slowEntries.length > 0) {
      logger.warn('Slow operations detected:', slowEntries)
    }
  }
}

// Singleton instance
export const performanceMonitor = new PerformanceMonitor()

// React hook for performance monitoring
export function usePerformanceMonitor() {
  const startMeasure = (name: string) => performanceMonitor.startMeasure(name)
  const endMeasure = (name: string) => performanceMonitor.endMeasure(name)
  const getMetrics = () => performanceMonitor.getMetrics()
  const getEntries = () => performanceMonitor.getEntries()
  const getSlowEntries = (threshold?: number) => performanceMonitor.getSlowEntries(threshold)
  const clearEntries = () => performanceMonitor.clearEntries()
  const reportToAnalytics = () => performanceMonitor.reportToAnalytics()

  return {
    startMeasure,
    endMeasure,
    getMetrics,
    getEntries,
    getSlowEntries,
    clearEntries,
    reportToAnalytics
  }
}

// Performance budget checker
export class PerformanceBudget {
  private budgets = {
    loadTime: 3000, // 3 seconds
    renderTime: 1000, // 1 second
    bundleSize: 500000, // 500KB
    memoryUsage: 50000000, // 50MB
    networkRequests: 50,
    cacheHitRate: 80 // 80%
  }

  public checkBudget(metrics: PerformanceMetrics): {
    passed: boolean
    violations: string[]
    score: number
  } {
    const violations: string[] = []
    let score = 100

    if (metrics.loadTime > this.budgets.loadTime) {
      violations.push(`Load time exceeded budget: ${metrics.loadTime}ms > ${this.budgets.loadTime}ms`)
      score -= 20
    }

    if (metrics.renderTime > this.budgets.renderTime) {
      violations.push(`Render time exceeded budget: ${metrics.renderTime}ms > ${this.budgets.renderTime}ms`)
      score -= 15
    }

    if (metrics.bundleSize > this.budgets.bundleSize) {
      violations.push(`Bundle size exceeded budget: ${metrics.bundleSize} bytes > ${this.budgets.bundleSize} bytes`)
      score -= 25
    }

    if (metrics.memoryUsage > this.budgets.memoryUsage) {
      violations.push(`Memory usage exceeded budget: ${metrics.memoryUsage} bytes > ${this.budgets.memoryUsage} bytes`)
      score -= 10
    }

    if (metrics.networkRequests > this.budgets.networkRequests) {
      violations.push(`Network requests exceeded budget: ${metrics.networkRequests} > ${this.budgets.networkRequests}`)
      score -= 10
    }

    if (metrics.cacheHitRate < this.budgets.cacheHitRate) {
      violations.push(`Cache hit rate below budget: ${metrics.cacheHitRate}% < ${this.budgets.cacheHitRate}%`)
      score -= 20
    }

    return {
      passed: violations.length === 0,
      violations,
      score: Math.max(0, score)
    }
  }

  public getBudgets() {
    return { ...this.budgets }
  }
}

export const performanceBudget = new PerformanceBudget()

// Web Vitals monitoring
// Disabled to prevent console logging - can be re-enabled if analytics integration is needed
export function measureWebVitals() {
  // Web vitals measurement disabled to prevent console logging
  // To re-enable, uncomment below and configure analytics service
  /*
  if (typeof window !== 'undefined') {
    import('web-vitals').then(({ onCLS, onFID, onFCP, onLCP, onTTFB }) => {
      // Send metrics to analytics service instead of console
      const sendToAnalytics = (metric: any) => {
        // Example: Send to your analytics service
        // analytics.track('web-vital', metric);
      }
      onCLS(sendToAnalytics)
      onFID(sendToAnalytics)
      onFCP(sendToAnalytics)
      onLCP(sendToAnalytics)
      onTTFB(sendToAnalytics)
    }).catch(() => {
      // Web vitals not available
    })
  }
  */
}

// Bundle analyzer helper
export function analyzeBundle() {
  if (import.meta.env.DEV) {
    console.group('Bundle Analysis')
    
    // Check for large dependencies
    const scripts = document.querySelectorAll('script[src]')
    scripts.forEach(script => {
      const src = script.getAttribute('src')
      if (src && src.includes('chunk')) {
        logger.info('Chunk loaded:', src)
      }
    })
    
    // Check for unused imports (development only)
    if ((window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      logger.info('React DevTools available for component analysis')
    }
    
    console.groupEnd()
  }
}
